// src/encounter/lib/care-plan-view.ts
//
// The view model the encounter's care-plan card will render (contract C5 → the card). There is NO
// card yet: the freeze manifest has no anchor for one, and the two DS candidates are off-limits
// (CarePlanProgress is quarantined, GoalProgressCard is P2). The card is a design gap for a Claude
// Design pass; this file is the data it will be handed, so the design can start from real fields.
//
// CLINICAL RULES THIS MAPPER HOLDS (spec §3, settled 29 Sep):
//   · Goal status is the SERVER's verdict, computed on read from the latest coded lab result. It is
//     carried, never recomputed here — a client that re-derives "met" from value vs target is a
//     second, silently divergent rule. So is its `reason` (a no-data code; the card owns the words).
//   · The verdict is "as of" the latest result's date (`asOf`). No window: an old result still decides.
//   · A result with only the lab's text (`valueText`, e.g. "<5.0") IS a result — shown as that text,
//     never as "no result".
//   · The word is "target". Every target names its source; a target with no source is kept, with
//     `source: null`, so the card can say so rather than present it as authoritative.
//   · The comparators are exactly the server's: < <= > >= and the typographic ≤ ≥ (carried as sent).
//     An unreadable target (any other comparator, '=' included; a non-numeric value) becomes
//     `target: null` — it is never rendered as if it were a valid number (the server answers such a
//     goal no-data, reason unsupported-comparator / no-target). An unknown goal status becomes
//     'unknown', never 'no-data': "no result on file" and "the server said something we do not
//     understand" are different facts, and only the first one is a clinical statement.
//   · A plan or goal with no id is a contract break, and it FAILS the read. Dropping it instead
//     would let `[{}]` arrive at the card as "no care plan", or a plan arrive one goal short.
import type { CarePlanActivityWire, CarePlanGoalWire, CarePlanWire, GoalLatestWire, GoalTargetWire, PatientEnrolmentWire } from '@/types/care-model-api';

/** Exactly the server's list (Scrips.Patient CarePlanGoalRules): ≤ and ≥ are its <= and >=. No '='. */
export type GoalComparator = '<' | '<=' | '>' | '>=' | '≤' | '≥';
export type GoalStatus = 'met' | 'not-met' | 'no-data' | 'unknown';

export interface GoalTargetView {
  comparator: GoalComparator;
  value: number;
  unit: string;
  /** Guideline or clinician that set the target. null = the server sent none; the card must say so. */
  source: string | null;
}

export interface GoalLatestView {
  /** null when the lab reported text only — then `valueText` is the result. */
  value: number | null;
  /** The lab's own text value (e.g. "<5.0"). The card shows it as the lab wrote it. */
  valueText: string | null;
  unit: string;
  /** When the result was taken, as the lab reported it (ISO string, unformatted). */
  date: string;
  observationId: string | null;
}

export interface GoalView {
  id: string;
  /** The authored care-model node this goal came from. */
  sourceNodeId: string | null;
  title: string;
  loinc: string | null;
  target: GoalTargetView | null;
  /** The server sent something for the target (a value or a comparator). false = the goal has no target; target null + true = unreadable. */
  targetSet: boolean;
  latest: GoalLatestView | null;
  status: GoalStatus;
  /** The server's no-data code, carried verbatim; null when met or not-met. */
  reason: string | null;
  /** The status is AS OF this date — the latest result's (ISO string, unformatted). null = no result. */
  asOf: string | null;
  /**
   * FHIR Goal.target.dueDate (ISO string, unformatted). null = none set. On the GOAL, not the target view:
   * an unreadable target (null) must not take the due date with it (Andrew's review of #506).
   */
  dueDate: string | null;
  /** L360 A1: the stored lifecycle status. Optional so views built before C5 carried it stay valid. */
  lifecycleStatus?: string | null;
  /** L360 A1: the server's track, when it sent one (the panel falls back to the sourceNodeId prefix). */
  track?: string | null;
  /** L360 A1: who signed and when. Non-null = SIGNED; absent or null = PROPOSED. */
  signoff?: { signedBy: string; signedAt: string } | null;
}

/**
 * Where the patient is in the care model, from C4 (the patient's active enrolments; C5 carries no
 * step), joined on the care-model definition id by `withCurrentSteps`:
 *   'known'     → an active enrolment of this plan's model is waiting on this step.
 *   'none'      → C4 answered and no active enrolment of this model is waiting on a step.
 *   'not-wired' → no step source was read (C4 failed or has not answered). Rendered as nothing,
 *                 never as "no step".
 */
export type CarePlanCurrentStep =
  | { state: 'known'; name: string }
  | { state: 'none' }
  | { state: 'not-wired' };

export interface CarePlanView {
  carePlanId: string;
  /** The care model's title, as the plan carries it. */
  title: string;
  /** FHIR CarePlan.status, lower-cased; 'unknown' when absent. */
  status: string;
  definitionId: string | null;
  period: { start: string | null; end: string | null } | null;
  currentStep: CarePlanCurrentStep;
  goals: GoalView[];
  /** L360 A1: the plan's content revision. null = the server sent none. */
  revision?: string | null;
  /** L360 A2: named contributors. */
  contributors?: { practitionerId: string; role: string }[];
  /** L360 A2: false = this environment records no contributors (say so, never "no team"). */
  contributorsAvailable?: boolean | null;
  /** B1: the plan's activities. Absent = the server records none (say so); [] = none on the plan. */
  activities?: CarePlanActivityView[];
}

export type CarePlanLane = 'physician-orders' | 'nutrition' | 'exercise' | 'coaching' | 'supplements' | 'physiotherapy';
export const CARE_PLAN_LANE_IDS: readonly CarePlanLane[] = ['physician-orders', 'nutrition', 'exercise', 'coaching', 'supplements', 'physiotherapy'];
export type CarePlanActivityStatus = 'ai-draft' | 'draft' | 'accepted' | 'signed';

export interface CarePlanActivityView {
  id: string;
  title: string;
  lane: CarePlanLane;
  kind: 'activity' | 'retest';
  ownerPractitionerId: string | null;
  /** The owner's user id; the server alone decides who may accept or sign (403 { reason } otherwise). */
  ownerUserId: string | null;
  ownerRole: string | null;
  weekFrom: number;
  weekTo: number;
  status: CarePlanActivityStatus;
  signedBy: string | null;
  signedAt: string | null;
  revision: number | null;
}

const COMPARATORS: readonly GoalComparator[] = ['<', '<=', '>', '>=', '≤', '≥'];
const STATUSES: readonly GoalStatus[] = ['met', 'not-met', 'no-data'];

const text = (v: string | null | undefined): string | null => {
  const t = v?.trim();
  return t ? t : null;
};

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function toTarget(t: GoalTargetWire | null | undefined): GoalTargetView | null {
  if (!t || !isNumber(t.value)) return null;
  const comparator = COMPARATORS.find((c) => c === t.comparator?.trim());
  if (!comparator) return null;
  return {
    comparator,
    value: t.value,
    unit: t.unit?.trim() ?? '',
    source: text(t.source),
  };
}

/** Thrown for a C5 payload the card cannot represent faithfully. The hook surfaces it as a failed read. */
export class CarePlanContractError extends Error {}

/** A dated value or a dated lab text is a result; anything else is shown as no result, never guessed. */
function toLatest(l: GoalLatestWire | null | undefined): GoalLatestView | null {
  const date = text(l?.date);
  if (!l || !date) return null;
  const value = isNumber(l.value) ? l.value : null;
  const valueText = text(l.valueText);
  if (value === null && valueText === null) return null;
  return { value, valueText, unit: l.unit?.trim() ?? '', date, observationId: text(l.observationId) };
}

function toSignoff(s: CarePlanGoalWire['signoff']): { signedBy: string; signedAt: string } | null {
  const signedBy = text(s?.signedBy);
  const signedAt = text(s?.signedAt);
  return signedBy && signedAt ? { signedBy, signedAt } : null;
}

function toGoal(g: CarePlanGoalWire): GoalView {
  const id = text(g.id);
  if (!id) throw new CarePlanContractError('care-plans: a goal has no id');
  const latest = toLatest(g.latest);
  return {
    id,
    sourceNodeId: text(g.sourceNodeId),
    title: text(g.title) ?? 'Untitled goal',
    loinc: text(g.loinc),
    target: toTarget(g.target),
    targetSet: isNumber(g.target?.value) || text(g.target?.comparator) !== null,
    latest,
    status: STATUSES.find((s) => s === g.status) ?? 'unknown',
    reason: text(g.reason),
    asOf: latest?.date ?? null,
    dueDate: text(g.target?.dueDate),
    // The L360 fields are carried only when the server sent them, so a pre-C5-addition payload maps as before.
    ...(g.lifecycleStatus !== undefined ? { lifecycleStatus: text(g.lifecycleStatus) } : {}),
    ...(g.track !== undefined ? { track: text(g.track) } : {}),
    ...(g.signoff !== undefined ? { signoff: toSignoff(g.signoff) } : {}),
  };
}

const ACTIVITY_STATUSES: readonly CarePlanActivityStatus[] = ['ai-draft', 'draft', 'accepted', 'signed'];

/** One activity, or null when it cannot be placed (no id, unknown lane or status, no readable weeks) or is cancelled. */
function toActivity(a: CarePlanActivityWire): CarePlanActivityView | null {
  const id = text(a.id);
  const lane = CARE_PLAN_LANE_IDS.find((l) => l === text(a.lane)?.toLowerCase());
  const status = ACTIVITY_STATUSES.find((s) => s === text(a.status)?.toLowerCase());
  const from = isNumber(a.weekFrom) ? Math.max(1, Math.round(a.weekFrom)) : null;
  const to = isNumber(a.weekTo) ? Math.max(from ?? 1, Math.round(a.weekTo)) : from;
  if (!id || !lane || !status || from === null || to === null) return null;
  return {
    id,
    title: text(a.title) ?? 'Plan item',
    lane,
    kind: text(a.kind)?.toLowerCase() === 'retest' ? 'retest' : 'activity',
    ownerPractitionerId: text(a.ownerPractitionerId),
    ownerUserId: text(a.ownerUserId),
    ownerRole: text(a.ownerRole),
    weekFrom: from,
    weekTo: to,
    status,
    signedBy: text(a.signedBy),
    signedAt: text(a.signedAt),
    revision: isNumber(a.revision) ? a.revision : null,
  };
}

function toCarePlan(p: CarePlanWire): CarePlanView {
  const carePlanId = text(p.carePlanId);
  if (!carePlanId) throw new CarePlanContractError('care-plans: a plan has no carePlanId');
  return {
    carePlanId,
    title: text(p.title) ?? 'Care plan',
    status: text(p.status)?.toLowerCase() ?? 'unknown',
    definitionId: text(p.definitionId),
    period: p.period ? { start: text(p.period.start), end: text(p.period.end) } : null,
    currentStep: { state: 'not-wired' },
    goals: (p.goals ?? []).map(toGoal),
    ...(p.revision !== undefined ? { revision: text(p.revision) } : {}),
    ...(Array.isArray(p.contributors)
      ? {
          contributors: p.contributors.flatMap((c) => {
            const practitionerId = text(c.practitionerId);
            const role = text(c.role);
            return practitionerId && role ? [{ practitionerId, role }] : [];
          }),
        }
      : {}),
    ...(typeof p.contributorsAvailable === 'boolean' ? { contributorsAvailable: p.contributorsAvailable } : {}),
    ...(Array.isArray(p.activities)
      ? {
          activities: p.activities.flatMap((a) => {
            const v = toActivity(a);
            return v ? [v] : [];
          }),
        }
      : {}),
  };
}

/**
 * C5 payload → the card's view model. Unreadable PARTS are nulled rather than guessed; a row with no
 * id throws `CarePlanContractError` (see the header), so nothing is silently lost.
 */
export function toCarePlanViews(raw: readonly CarePlanWire[]): CarePlanView[] {
  return raw.map(toCarePlan);
}

/** A plan's definition id may be the bare Elsa id or a canonical ending in it ("PlanDefinition/<id>"). */
function sameDefinition(planDefinitionId: string | null, enrolmentDefinitionId: string | null | undefined): boolean {
  const e = text(enrolmentDefinitionId);
  if (!planDefinitionId || !e) return false;
  return planDefinitionId === e || planDefinitionId.endsWith(`/${e}`);
}

/** The care model a definition id names: its last path segment ("PlanDefinition/<id>" and "<id>" agree). */
function definitionKey(id: string | null): string | null {
  return text(id)?.split('/').pop() ?? null;
}

/**
 * Joins each plan to its current step from C4. `enrolments` null = C4 was not read (failed or still
 * loading): every plan keeps 'not-wired'.
 *
 * C5 carries no enrolment id, so the join is by care model, and a step is 'known' only when that join
 * is unambiguous: the plan is ACTIVE, it is the only active plan of its care model, and that model has
 * exactly ONE active enrolment, which waits on a named step. A completed plan of a model the patient was
 * re-enrolled in must never borrow the new enrolment's step; an ambiguous join shows no step, never a
 * wrong one.
 */
export function withCurrentSteps(
  plans: readonly CarePlanView[],
  enrolments: readonly PatientEnrolmentWire[] | null,
): CarePlanView[] {
  if (enrolments === null) return plans.map((p) => ({ ...p, currentStep: { state: 'not-wired' } }));
  const stepOf = (p: CarePlanView): string | null => {
    if (p.status !== 'active') return null;
    const key = definitionKey(p.definitionId);
    if (plans.filter((o) => o.status === 'active' && definitionKey(o.definitionId) === key).length !== 1) return null;
    // Uniqueness is counted over EVERY active enrolment of the model, named step or not: an unnamed
    // second enrolment still makes the association ambiguous.
    const active = enrolments.filter((e) => sameDefinition(p.definitionId, e.definitionId) && text(e.status)?.toLowerCase() === 'active');
    return active.length === 1 ? text(active[0].currentStep?.name) : null;
  };
  return plans.map((p) => {
    const name = stepOf(p);
    return { ...p, currentStep: name ? { state: 'known', name } : { state: 'none' } };
  });
}
